// Project-Card for S2 home grid. Shows color stripe, name, owner-avatar,
// task progress, status badge and counts. Click → /p/:id.

import { Link } from "wouter-preact";
import { Star, StarOff } from "lucide-preact";
import type { Project, ProjectListItem } from "@juuplan/shared";
import { useUsers, findUser } from "../store/users";
import { useFavorites, toggleFavorite } from "../store/favorites";
import {
  formatDateShort,
  PROJECT_STATUS_LABEL,
  PROJECT_STATUS_VAR,
  softPillStyle,
} from "../lib/format";
import { UserAvatar } from "./UserAvatar";
import { HealthBadge } from "./HealthBadge";

interface Props {
  /** Either a bare Project or a list-item with counts; we read counts if present. */
  project: Project | ProjectListItem;
  /** Override the auto-detected counts (used when caller already knows them). */
  taskTotal?: number;
  taskDone?: number;
}

function hasCounts(p: Project | ProjectListItem): p is ProjectListItem {
  return "tasks_total" in p && "tasks_done" in p;
}

export function ProjectCard(props: Props): preact.JSX.Element {
  const { project, taskTotal, taskDone } = props;
  useUsers(); // ensure cache populated
  const favorites = useFavorites();
  const owner = findUser(project.owner_id);

  const total = taskTotal ?? (hasCounts(project) ? project.tasks_total : 0);
  const done = taskDone ?? (hasCounts(project) ? project.tasks_done : 0);
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const isFavorite = favorites.project.has(project.id);

  // Favoriten-Knopf als GESCHWISTER des Links, nicht darin: ein <button> in
  // einem <a> ist verschachtelte Interaktion (ungueltiges HTML, Screenreader
  // lesen „Link, Schaltflaeche" durcheinander).
  return (
    <div class="group relative">
    <Link
      href={`/p/${project.id}`}
      class="relative flex h-full flex-col rounded-lg bg-[color:var(--color-surface-container-lowest)] border border-[color:var(--color-outline-variant)] hover:border-[color:var(--color-outline)] hover:shadow-level-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 transition-[border-color,box-shadow] duration-150 overflow-hidden"
    >
      <div
        class="h-1 w-full"
        style={{ background: project.color ?? PROJECT_STATUS_VAR[project.status] }}
        aria-hidden="true"
      />
      <div class="p-4 flex flex-1 flex-col gap-3">
        {/* pr-7: Platz fuer den Favoriten-Stern oben rechts, der sonst beim
            Hover ueber dem Status-Abzeichen lag. */}
        <header class="flex items-start gap-3 pr-7">
          <h3
            class="flex-1 min-w-0 text-base font-semibold text-[color:var(--color-on-surface)] tracking-tight line-clamp-2 [overflow-wrap:anywhere]"
            title={project.name}
          >
            {project.name}
          </h3>
          <HealthBadge
            variant="dot"
            project={{
              status: project.status,
              start_date: project.start_date,
              target_date: project.target_date,
              tasks_total: total,
              tasks_done: done,
            }}
          />
          <StatusBadge status={project.status} />
        </header>

        {project.description && (
          <p class="text-xs text-[color:var(--color-on-surface-variant)] line-clamp-2">
            {project.description}
          </p>
        )}

        <div>
          <div class="flex items-center justify-between text-label text-[color:var(--color-on-surface-variant)] mb-1">
            <span>Fortschritt</span>
            <span class="tabular-nums">
              {done} / {total} ({pct}%)
            </span>
          </div>
          <div class="h-1.5 rounded bg-[color:var(--color-surface-container-low)] overflow-hidden">
            <div
              class="h-full rounded"
              style={{
                width: `${pct}%`,
                background: project.color ?? "var(--color-primary)",
              }}
              aria-hidden="true"
            />
          </div>
        </div>

        <footer class="mt-auto flex items-center justify-between gap-2 pt-1">
          <div class="flex items-center gap-2 min-w-0">
            <UserAvatar userId={project.owner_id} size={24} />
            <span class="text-xs text-[color:var(--color-on-surface-variant)] truncate">
              {owner?.name ?? `User #${project.owner_id}`}
            </span>
          </div>
          <span class="text-label text-[color:var(--color-on-surface-variant)] shrink-0">
            {project.target_date ? `Ziel: ${formatDateShort(project.target_date)}` : ""}
          </span>
        </footer>
      </div>
    </Link>
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          void toggleFavorite("project", project.id).catch(() => {
            /* toggleFavorite() hat schon gemeldet */
          });
        }}
        class={`absolute top-3 right-2 z-10 inline-flex items-center justify-center rounded p-1.5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-1 ${
          isFavorite
            ? "text-[color:var(--color-warning,#A16207)] hover:bg-[color:var(--color-surface-container-low)]"
            : "text-[color:var(--color-on-surface-variant)] opacity-0 group-hover:opacity-100 hover:bg-[color:var(--color-surface-container-low)] hover:text-[color:var(--color-on-surface)] focus:opacity-100"
        }`}
        aria-label={isFavorite ? "Aus Favoriten entfernen" : "Zu Favoriten hinzufügen"}
        aria-pressed={isFavorite}
        title={isFavorite ? "Favorit entfernen" : "Als Favorit markieren"}
      >
        {isFavorite ? (
          <Star size={14} fill="currentColor" aria-hidden="true" />
        ) : (
          <StarOff size={14} aria-hidden="true" />
        )}
      </button>
    </div>
  );
}

export function StatusBadge(props: { status: Project["status"] }): preact.JSX.Element {
  return (
    <span
      class="inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-label font-semibold shrink-0"
      style={softPillStyle(PROJECT_STATUS_VAR[props.status])}
    >
      <span
        class="inline-block size-1.5 rounded-full shrink-0"
        style={{ background: PROJECT_STATUS_VAR[props.status] }}
        aria-hidden="true"
      />
      {PROJECT_STATUS_LABEL[props.status]}
    </span>
  );
}
